/**
 * @file DealCreateDialog.tsx
 * @description Окно «Новая сделка» (phase19.31, макет (в): «+ Сделка» в
 * реестре и на доске — одно и то же действие).
 *
 * Поля: название, клиент (выбор в боковой панели — общий `CustomerPickerDialog`),
 * воронка (`SelectField`, по умолчанию — воронка по умолчанию или та, что
 * выбрана на доске) и необязательная сумма. Стадия — начальная стадия
 * воронки, ответственный — текущий сотрудник (так решает сервер).
 * Ошибки — под полями; ошибка сервера — уведомлением человеческим текстом.
 * На телефоне окно на весь экран.
 */

import { UserRound } from "lucide-react";
import { useState } from "react";

import { CustomerPickerDialog } from "../../orders/CustomerPickerDialog";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { useNotify } from "../../ui/NotifyProvider";
import { SelectField } from "../../ui/SelectField";
import { ApiError } from "../../../lib/api-client";
import { createDeal, type DealDto, type PipelineDto } from "../../../lib/api-crm";
import type { CustomerDto } from "../../../lib/api-customers";
import { validateDealDraft, type DealDraft, type DealDraftErrors } from "../../../lib/crm-deal-create";
import { customerTitle } from "../../../lib/crm-customer-view";

interface Props {
  pipelines: PipelineDto[];
  /** Воронка, выбранная заранее (доска); иначе — воронка по умолчанию. */
  initialPipelineId?: string;
  onClose: () => void;
  onCreated: (deal: DealDto) => void;
}

/** Подпись ошибки под полем. */
function FieldError({ id, text }: { id: string; text?: string }) {
  return text ? (
    <p id={id} className="type-caption text-[var(--status-error-fg)]">
      {text}
    </p>
  ) : null;
}

export function DealCreateDialog({ pipelines, initialPipelineId, onClose, onCreated }: Props) {
  const notify = useNotify();
  const fallbackPipeline = pipelines.find((p) => p.isDefault)?.id ?? pipelines[0]?.id ?? "";
  const [draft, setDraft] = useState<DealDraft>({
    displayName: "",
    customerId: "",
    pipelineId: initialPipelineId || fallbackPipeline,
    amount: "",
  });
  const [customer, setCustomer] = useState<CustomerDto | null>(null);
  const [picking, setPicking] = useState(false);
  const [errors, setErrors] = useState<DealDraftErrors>({});
  const [saving, setSaving] = useState(false);

  const patch = (next: Partial<DealDraft>) => setDraft((prev) => ({ ...prev, ...next }));

  async function submit() {
    const { errors: found, payload } = validateDealDraft(draft);
    setErrors(found);
    if (!payload) return;
    setSaving(true);
    try {
      const deal = await createDeal(payload);
      notify.success("Сделка создана");
      onCreated(deal);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось создать сделку. Попробуйте ещё раз");
    } finally {
      setSaving(false);
    }
  }

  return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent fullScreen>
          <DialogHeader>
            <DialogTitle>Новая сделка</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="deal-name">Название</Label>
              <Input
                id="deal-name"
                value={draft.displayName}
                onChange={(e) => patch({ displayName: e.target.value })}
                placeholder="Мария Ч. — подарочный набор"
                aria-invalid={errors.displayName ? true : undefined}
                aria-describedby={errors.displayName ? "deal-name-error" : undefined}
                autoFocus
              />
              <FieldError id="deal-name-error" text={errors.displayName} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="deal-customer">Клиент</Label>
              <Button id="deal-customer" type="button" variant="outline" className="justify-start" onClick={() => setPicking(true)}>
                <UserRound className="size-4" aria-hidden />
                <span className="truncate">{customer ? customerTitle(customer) : "Выбрать клиента"}</span>
              </Button>
              <FieldError id="deal-customer-error" text={errors.customerId} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="deal-pipeline">Воронка</Label>
              <SelectField
                id="deal-pipeline"
                ariaLabel="Воронка"
                value={draft.pipelineId}
                onChange={(pipelineId) => patch({ pipelineId })}
                options={pipelines.map((p) => ({ value: p.id, label: p.name }))}
                placeholder="Выберите воронку"
              />
              <FieldError id="deal-pipeline-error" text={errors.pipelineId} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="deal-amount">Сумма, ₽ (необязательно)</Label>
              <Input
                id="deal-amount"
                inputMode="decimal"
                value={draft.amount}
                onChange={(e) => patch({ amount: e.target.value })}
                placeholder="7 500"
                aria-invalid={errors.amount ? true : undefined}
                aria-describedby={errors.amount ? "deal-amount-error" : undefined}
              />
              <FieldError id="deal-amount-error" text={errors.amount} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Отмена
              </Button>
              <Button type="submit" loading={saving}>
                Создать сделку
              </Button>
            </DialogFooter>
          </form>
          {/* Панель выбора клиента — внутри окна (вложенный диалог): нажатие
              в ней не считается «мимо окна» и не закрывает форму. */}
          {picking ? (
            <CustomerPickerDialog
              onClose={() => setPicking(false)}
              onSelect={(c) => {
                setCustomer(c);
                patch({ customerId: c.id });
                setPicking(false);
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
  );
}
